import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Table,
  Spin,
  Alert,
  Tag,
  Divider,
  Row,
  Col,
  Card,
  Empty,
  Space,
  Tooltip,
  Badge,
} from "antd";
import {
  GoogleOutlined,
  EnvironmentOutlined,
  ShoppingOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import NumberFormat from "react-number-format";
import { DateTime } from "luxon";

const ClusterAcceptanceModal = ({
  visible,
  cluster,
  onAccept,
  onCancel,
  loading,
  bikerVehicleType,
}) => {
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [acceptSuccess, setAcceptSuccess] = useState(false);

  const handleAcceptCluster = async () => {
    setAcceptLoading(true);
    setAcceptError(null);

    try {
      await onAccept(cluster.id);
      setAcceptSuccess(true);

      // Auto-close after success
      setTimeout(() => {
        handleModalClose();
      }, 2000);
    } catch (error) {
      setAcceptError(
        error?.response?.data?.message ||
          "Failed to accept cluster. Another delivery may have already accepted it."
      );
      setAcceptLoading(false);
    }
  };

  const handleModalClose = () => {
    setAcceptLoading(false);
    setAcceptError(null);
    setAcceptSuccess(false);
    onCancel();
  };

  const canAcceptCluster = () => {
    const eligibleVehicles = ["car", "van"];
    return eligibleVehicles.includes(bikerVehicleType?.toLowerCase());
  };

  const vehicleEligible = canAcceptCluster();

  const orderColumns = [
    {
      title: "Order Ref",
      dataIndex: ["track_id"],
      key: "track_id",
      width: 120,
      render: (text) => (
        <Tag color="blue">
          <strong>{text}</strong>
        </Tag>
      ),
    },
    {
      title: "Customer",
      dataIndex: ["user", "first_name"],
      key: "customer_name",
      width: 150,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Delivery Address",
      dataIndex: ["delivery", "address"],
      key: "address",
      render: (text) => (
        <div style={{ fontSize: "12px", color: "#666" }}>
          <EnvironmentOutlined /> {text}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: ["settleAmount"],
      key: "amount",
      width: 100,
      align: "right",
      render: (amount) => (
        <NumberFormat
          value={Number(amount).toFixed(2)}
          displayType={"text"}
          thousandSeparator={true}
          prefix={"₦"}
          style={{ fontWeight: "bold" }}
        />
      ),
    },
  ];

  const StatCard = ({ icon: Icon, title, value, color }) => (
    <Card
      size="small"
      style={{
        textAlign: "center",
        borderTop: `4px solid ${color}`,
        backgroundColor: "#fafafa",
      }}
    >
      <Icon style={{ fontSize: "20px", color, marginBottom: "8px" }} />
      <p style={{ margin: "8px 0 4px", fontSize: "12px", color: "#999" }}>
        {title}
      </p>
      <p style={{ margin: "0", fontSize: "18px", fontWeight: "bold" }}>
        {value}
      </p>
    </Card>
  );

  if (!cluster) {
    return null;
  }

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Badge
            count={cluster?.totalOrders}
            style={{ backgroundColor: "#52c41a" }}
          />
          <span>
            Delivery Cluster{" "}
            <strong style={{ color: "#1890ff" }}>{cluster?.clusterId}</strong>
          </span>
        </div>
      }
      visible={visible}
      onCancel={handleModalClose}
      width="95%"
      footer={null}
      bodyStyle={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto" }}
      closeIcon={!acceptLoading}
    >
      {/* SUCCESS STATE */}
      {acceptSuccess && (
        <Alert
          message="✅ Cluster Accepted Successfully!"
          description="You can now view this cluster in your deliveries. Good luck!"
          type="success"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      )}

      {/* ERROR STATE */}
      {acceptError && (
        <Alert
          message="❌ Failed to Accept Cluster"
          description={acceptError}
          type="error"
          showIcon
          style={{ marginBottom: "16px" }}
          closable
          onClose={() => setAcceptError(null)}
        />
      )}

      {/* VEHICLE TYPE WARNING */}
      {!vehicleEligible && (
        <Alert
          message="⚠️ Vehicle Type Not Eligible"
          description={`Your vehicle type (${bikerVehicleType}) cannot accept next-day clusters. Only Car and Van vehicles are eligible.`}
          type="warning"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      )}

      {/* DELIVERY DATE INFO */}
      {cluster?.deliveryDate && (
        <Alert
          message={`📅 Delivery Date: ${DateTime.fromJSDate(
            new Date(cluster.deliveryDate)
          ).toLocaleString(DateTime.DATE_HUGE)}`}
          description="⏰ You must confirm ready by 9AM on this date"
          type="info"
          showIcon
          style={{ marginBottom: "16px" }}
        />
      )}

      {/* STATISTICS */}
      <div style={{ marginBottom: "24px" }}>
        <h3
          style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}
        >
          📊 Cluster Statistics
        </h3>
        <Row gutter={[12, 12]}>
          <Col xs={12} sm={6}>
            <StatCard
              icon={ShoppingOutlined}
              title="Total Orders"
              value={cluster?.totalOrders || 0}
              color="#1890ff"
            />
          </Col>
          <Col xs={12} sm={6}>
            <StatCard
              icon={EnvironmentOutlined}
              title="Delivery Stops"
              value={cluster?.totalStops || 0}
              color="#52c41a"
            />
          </Col>
          <Col xs={12} sm={6}>
            <StatCard
              icon={DollarOutlined}
              title="Total Value"
              value={
                <NumberFormat
                  value={cluster?.totalValue}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix="₦"
                />
              }
              color="#fa8c16"
            />
          </Col>
          <Col xs={12} sm={6}>
            <StatCard
              icon={ClockCircleOutlined}
              title="Est. Time"
              value={`${cluster?.totalStops * 15}-${cluster?.totalStops * 25}m`}
              color="#eb2f96"
            />
          </Col>
        </Row>
      </div>

      <Divider />

      {/* DESTINATIONS MAP PLACEHOLDER */}
      {cluster?.destinations && cluster.destinations.length > 0 && (
        <div style={{ marginBottom: "24px" }}>
          <h3
            style={{
              marginBottom: "12px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            🗺️ Delivery Destinations
          </h3>
          <div
            style={{
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              padding: "16px",
              minHeight: "200px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <GoogleOutlined style={{ fontSize: "32px", color: "#bfbfbf" }} />
            <p style={{ marginTop: "12px", color: "#8c8c8c" }}>
              🚧 Map integration coming soon
            </p>
            <p style={{ color: "#bfbfbf", fontSize: "12px" }}>
              {cluster.destinations.length} location(s) in this cluster
            </p>
          </div>
        </div>
      )}

      <Divider />

      {/* ORDERS LIST */}
      <div style={{ marginBottom: "24px" }}>
        <h3
          style={{ marginBottom: "12px", fontSize: "14px", fontWeight: "600" }}
        >
          📦 Orders in This Cluster
        </h3>
        {cluster?.orders && cluster.orders.length > 0 ? (
          <Table
            columns={orderColumns}
            dataSource={cluster.orders}
            rowKey="id"
            pagination={{
              pageSize: 5,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} orders`,
            }}
            size="small"
            scroll={{ x: true }}
          />
        ) : (
          <Empty description="No orders in this cluster" />
        )}
      </div>

      <Divider />

      {/* INSTRUCTIONS */}
      <Alert
        message="How Cluster Delivery Works"
        description={
          <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
            <li>
              <strong>Accept:</strong> Click "Accept Cluster" to participate
            </li>
            <li>
              <strong>First-Click Wins:</strong> Only the first delivery to
              accept gets this cluster
            </li>
            <li>
              <strong>Delivery Date:</strong> Complete all deliveries on{" "}
              {cluster?.deliveryDate
                ? DateTime.fromJSDate(
                    new Date(cluster.deliveryDate)
                  ).toLocaleString(DateTime.DATE_MED)
                : "scheduled date"}
            </li>
            <li>
              <strong>9AM Deadline:</strong> Confirm ready by 9AM on delivery
              date
            </li>
            <li>
              <strong>Optimize Route:</strong> Plan your route to visit all
              stops efficiently
            </li>
          </ul>
        }
        type="info"
        showIcon
        style={{ marginBottom: "24px" }}
      />

      {/* ACTION BUTTONS */}
      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
        <Button onClick={handleModalClose} disabled={acceptLoading}>
          Cancel
        </Button>

        <Tooltip
          title={
            !vehicleEligible
              ? "Only Car and Van vehicles can accept next-day clusters"
              : acceptSuccess
              ? "Cluster accepted!"
              : "Click to accept this cluster (first-click wins!)"
          }
        >
          <Button
            type="primary"
            size="large"
            loading={acceptLoading}
            onClick={handleAcceptCluster}
            disabled={!vehicleEligible || acceptSuccess}
            danger={!vehicleEligible}
            icon={
              acceptSuccess ? (
                <CheckCircleOutlined />
              ) : acceptLoading ? (
                <LoadingOutlined />
              ) : (
                <WarningOutlined />
              )
            }
          >
            {acceptSuccess
              ? "✅ Cluster Accepted"
              : acceptLoading
              ? "Accepting..."
              : "🎯 Accept Cluster (First-Click Wins)"}
          </Button>
        </Tooltip>
      </Space>
    </Modal>
  );
};

export default ClusterAcceptanceModal;
